import { catchError, debounceTime } from 'rxjs/operators';
import { UploadService } from './../../../services/upload.service';
import { BbCanvasComponent } from './../../shared/bb-canvas/bb-canvas.component';
import { DateRange } from './../../../interfaces/dateRange';
import { OverlayService, NoticeDefinition } from './../../../services/overlay.service';
import { ContextMenuOption } from './../../shared/bb-context-menu/bb-context-menu.component';
import { HotkeysService, Hotkey } from 'angular2-hotkeys';
import { ProjectService } from './../../../services/project.service';
import { NgForm } from '@angular/forms';
import { ReduxService } from './../../../services/redux.service';
import { Mediaclip } from './../../../interfaces/mediaclip';
import { SapiService } from './../../../services/sapi.service';
import { FlowchartLink } from './../../../shared/models/flowchart-link.model';
import { ReplaySubject, Subscription } from 'rxjs';
import { FlowchartService } from './../../../services/flowchart.service';
import { FlowchartStep } from './../../../shared/models/flowchart-step.model';
import {
	ProjectFlowchart,
	ProjectStatsReference,
	Project,
	FlowchartGridRules
} from './../../../interfaces/project';
import { Component, ViewChild, Input, ElementRef, Renderer2, OnDestroy, OnInit } from '@angular/core';
import { ProjectFlowchartState } from '../../../shared/redux/project-flowchart.store';
import { TranslateService } from '@ngx-translate/core';
import { ChecklistItem } from '../../../interfaces/checklistItem';
import { FilterSet } from 'app/services/filter-set.service';

@Component({
	selector: 'flowchart-editor',
	templateUrl: './flowchart-editor.component.html',
	providers: [],
	styleUrls: ['./flowchart-editor.component.scss']
})

export class FlowchartEditorComponent implements OnInit, OnDestroy {
	@ViewChild('svg') svgEl: ElementRef;
	@ViewChild('svgWrapper') svgWrapper: ElementRef;
	@ViewChild('scrollerEl') scrollerEl: ElementRef;
	@ViewChild('formRef') set theForm(formEl: NgForm) {
		if (!this.formChangeSubscription && formEl && formEl.form) {
			this.inspectorForm = formEl;
			this.formChangeSubscription = formEl.form.valueChanges
				.pipe(debounceTime(1000))
				.subscribe((formChanges: any) => this.inspectorFormChanged(formEl, formChanges));
		}
	}
	@ViewChild('flowchartZoomCanvasRef')
	set setFlowchartZoomCanvas(el: BbCanvasComponent) {
		if (el) {
			if (!this.flowchartZoomCanvas) {
				this.flowchartZoomCanvas = el;
				this.initiateCanvas();
			}
		}
	}
	@ViewChild('stepDescriptionInputRef')
	set stepDescriptionInputEl(_input: ElementRef | undefined) {
		if (_input) {
			this.stepDescriptionInput = _input;
		}
	}

	@Input() flowchart: ProjectFlowchart;
	@Input() statsMode: boolean;
	@Input() haveProjectStepsStats: boolean;
	@Input() disableDragging: boolean;
	@Input() checklistItems: ChecklistItem[];

	autoZoomCanvas = true; // autoZoom is handled by [zoomToFit]="true" on flowchartZoomCanvasRef
	flowchartZoomCanvas: BbCanvasComponent;
	generatedFlowchart: ProjectFlowchart;
	inspectorForm: NgForm;
	stepDescriptionInput: ElementRef;

	selectedStep: FlowchartStep;
	newStep: any;
	currentMouseOverStep: FlowchartStep;
	currentMouseOverLink: string;
	fromStep: FlowchartStep;
	toStep: FlowchartStep;
	dragging: boolean;
	draggingNew: boolean;
	drawing: boolean;
	mouseOverPlaceholder: boolean;
	lines: any;

	formChangeSubscription: Subscription;
	mouseOutTimeout: any;
	mouseOverPlaceholderTimeout: any;
	noFlowchart: boolean;
	flowchartSize: any;
	maxStroke = 170;
	maxViewCount: number;
	selection: any = { items: {} };
	selectionSubscription: Subscription;
	dragPrevPos: any = { x: 0, y: 0 };
	groupDragDelta: any = { x: 0, y: 0 };
	dragJustEnded = false;
	selectDragging = false;
	snap: any = { x: 40, y: 20 };
	gridRules: FlowchartGridRules;
	ghostStep = {
		x: 0,
		y: 0,
		width: 135,
		height: 60,
		border: 2,
		fromId: null
	};
	zoomOfsettedStepClone: any;
	contextMenuOptions: ContextMenuOption[] = [];
	contextMenuStyle: any = { display: 'none' };
	highlightStepText = false;
	currentMousePosition: any = {};
	havePositionedCanvas = false;
	project: Project;
	mainMediaClip: Mediaclip;
	haveInitiatedCanvas = false;
	canvasStatus: { left: number; top: number; zoom: number };
	canvasEl: HTMLElement;
	highlightLine: FlowchartLink;
	fileUploads: any = {};

	statsRefSub: Subscription;
	statsRef: ProjectStatsReference;
	mainMediaClipSub: Subscription;
	canvasDragDisabled = true;
	statsColors: string[];
	statsColorRest: string;
	dropoffColor = '#BBBBBB';
	dropoffColorMo = '#7AB1DF';
	zoom: number;
	zoomFactor: number;
	occupiedCellRef: any = {};
	highlightCols: any[];
	clipSelectorFilterSet: FilterSet = [{
		filters: [{
			type: 'commonclip',
			field: 'mediatype',
			operator: 'is',
			value: '"video"'
		}]
	}];

	public snapObjectSubject: ReplaySubject<any>;
	public projectDownloadUrl: string;
	public projectStatsDates: string;
	private stateSubscription: Subscription;
	private projectWithStatsSubscription: Subscription;

	constructor(
		protected flowchartService: FlowchartService,
		protected sapiService: SapiService,
		protected overlayService: OverlayService,
		protected projectService: ProjectService,
		protected hotkeysService: HotkeysService,
		protected reduxService: ReduxService,
		protected uploadService: UploadService,
		protected _renderer: Renderer2,
		private translate: TranslateService,
		protected hostElement: ElementRef
	) {
		this.drawing = false;
		this.dragging = false;
		this.draggingNew = false;
		this.fromStep = null;
		this.lines = {};
		this.noFlowchart = false;
		this.flowchartSize = { width: 800, height: 450 };

		// dark to light
		this.statsColors = [
			'#BBBBBB'
		];
		this.statsColorRest = this.statsColors[0];
	}

	ngOnInit() {
		this.gridRules = this.flowchartService.getGridRules(this.statsMode);
		this.projectService.setStatsMode(this.statsMode);

		this.projectWithStatsSubscription = this.projectService.projectWithStatsSubject.subscribe((project: Project) => {
			if (project && project.id) {
				this.projectDownloadUrl = this.projectService.projectDownloadUrl + project.id;
			}
		});

		this.mainMediaClipSub = this.projectService.mainMediaClipSubject.subscribe((mediaClip: Mediaclip) => {
			this.mainMediaClip = mediaClip;
		});

		this.project = this.projectService.getProject();

		this.statsRefSub = this.projectService.statsRefSubject.subscribe((statsRef: ProjectStatsReference) => {
			if (statsRef) {
				this.statsRef = statsRef;
				this.projectStatsDates = this.projectService.getStatsQueryParamsString();
				this.statsUpdated();
			}
		});

		this.stateSubscription = this.reduxService.stateObs.subscribe((state: ProjectFlowchartState) => {
			if (state && state.data) {
				this.flowchart = { ...state.data };
				this.updateFlowchart(this.flowchart);
			}
		});

		this.selectionSubscription = this.flowchartService.selectionSubject.subscribe((selection) => {
			this.selection = { ...selection };

			this.highlightLine = null;
			Object.keys(this.selection.items).forEach((id: string) => {
				const item = this.selection.items[id];
				if (item && item.type === 'FlowchartLink') {
					this.highlightLine = item;
				}
			});
		});

		// placeHolder step:
		this.newStep = {
			x: -5000,
			y: -5000,
			width: 140,
			height: 60,
			stepsIn: [],
			stepsOut: [],
		};

		this.updateFlowchart(this.flowchart);
		this.bindHotKeys();
	}

	createMediaclipForUpload(file: any) {
		return this.sapiService.create('mediaclip', { originalfilename: file.name });
	}

	stepTitleIsNew(title: string) {
		return title.match(/(Click here to name your first step)|(^New (\d{1,4})$)/);
	}

	stepFilesDropped(step: FlowchartStep, files: FileList) {
		const file = files[0] as any;
		if (file.type.indexOf('video/') > -1) {
			file.stepId = step.id;
			this.fileUploads[file.stepId] = file;

			this.createMediaclipForUpload(file).subscribe((mediaclip: Mediaclip) => {
				const changedStep = { ...this.flowchart.steps[file.stepId] };
				changedStep.entityType = 'mediaclip';
				changedStep.entityId = mediaclip.id;
				if (file.name && this.stepTitleIsNew(changedStep.description)) {
					changedStep.description = file.name;
				}
				this.reduxService.dispatch({ type: 'UPDATE_STEP', step: changedStep });
				this.uploadService.handleFile(file, mediaclip);
			});
		} else {
			this.overlayService.createToast(this.translate.instant('ONLY_VIDEO_FILES_MESSAGE'));
		}
	}

	downloadFormData() {
		this.sapiService.download(
			'project',
			this.projectService.getProject().id,
			'formdata?' + this.projectService.getStatsQueryParamsString()
		).pipe(
			catchError((err: any) => {
				const notice: NoticeDefinition = {
					icon: 'warning',
					title: this.translate.instant('DOWNLOAD_FORM_DATA_FAILED'),
					contents: this.translate.instant('DOWNLOAD_FORM_DATA_FAILED_MESSAGE')
				};

				this.overlayService.createNotice(notice);
				return err;
			})
		).subscribe();
	}

	initiateCanvas() {
		this.positionCanvas();

		if (this.haveInitiatedCanvas || !this.flowchartZoomCanvas) {
			return;
		}

		this.haveInitiatedCanvas = true;
		this.flowchartZoomCanvas.canvasPositionObs.subscribe((data) => {
			this.zoomFactor = data.zoom;
			this.ghostStep.border = 1 + ((1 - data.zoom) * 3);
			this.canvasStatus = data;
			this.checkCanvasSize();
		});
	}

	checkCanvasSize() {
		this.flowchartSize = { width: 10, height: 10 };
		const flowchart = this.flowchart;

		Object.keys(flowchart.steps).forEach((stepId: string) => {
			flowchart.steps[stepId] = { ...flowchart.steps[stepId] };

			const step = flowchart.steps[stepId];
			let w = step && step.x + step.width + 250;
			if (step && step.col) {
				w = (step.col + 2) * (this.gridRules.x.gutter + this.gridRules.x.track);
			}
			if (w && w > this.flowchartSize.width) {
				this.flowchartSize.width = w;
			}
			let h = step && step.y + step.height + 150;
			if (step && step.row) {
				h = (step.row + 15) * (this.gridRules.y.gutter + this.gridRules.y.track);
			}
			if (h && h > this.flowchartSize.height) {
				this.flowchartSize.height = h;
			}
		});
		if (
			this.scrollerEl && this.scrollerEl.nativeElement && this.scrollerEl.nativeElement.offsetHeight
			&& this.canvasStatus && this.canvasStatus.zoom
		) {
			const frameHeight = this.scrollerEl.nativeElement.offsetHeight;
			const frameWidth = this.scrollerEl.nativeElement.offsetWidth;
			if (frameHeight / this.canvasStatus.zoom > this.flowchartSize.height) {
				this.flowchartSize.height = frameHeight / this.canvasStatus.zoom;
			}
			if (frameWidth / this.canvasStatus.zoom > this.flowchartSize.width) {
				this.flowchartSize.width = frameWidth / this.canvasStatus.zoom;
			}
		}
	}

	positionCanvas() {
		if (this.autoZoomCanvas || this.havePositionedCanvas || !this.flowchartZoomCanvas) {
			return;
		}

		setTimeout(() => {
			this.havePositionedCanvas = true;
			const wrapper = this.hostElement.nativeElement;
			const h = wrapper.querySelector('.steps-wrapper').offsetHeight;
			const t = wrapper.querySelector('.start-step') && wrapper.querySelector('.start-step').offsetTop;
			if (!t) {
				return;
			}
			const pct = (t / h) * 100;
			this.flowchartZoomCanvas.setZoom(100);
			setTimeout(() => {
				this.flowchartZoomCanvas.setCanvasPosition({
					x: 0,
					y: pct
				}, false, true);
				this.flowchartZoomCanvas.setZoom(100);
			}, 50);
		}, 50);
	}

	getStats(obj: any) {
		if (this.statsRef && this.statsRef[obj.entity] && this.statsRef[obj.entity][obj.property]) {
			if (obj.entity === 'link') {
				return this.statsRef[obj.entity];
			} else if (obj.entity === 'project') {
				return this.statsRef[obj.entity][obj.property];
			} else {
				return this.statsRef[obj.entity][obj.property][obj.id];
			}
		}
		return 0;
	}

	setFocus(andSelect: boolean) {
		setTimeout(() => {
			this.highlightStepText = true;
			if (this.stepDescriptionInput && this.stepDescriptionInput.nativeElement) {
				this.stepDescriptionInput.nativeElement.focus();
				if (andSelect) {
					this.stepDescriptionInput.nativeElement.select();
				}
			}
		});
	}

	bindHotKeys() {
		let hotkeyTimeout: any;
		this.hotkeysService.add(new Hotkey('shift', (event: KeyboardEvent): boolean => {
			clearTimeout(hotkeyTimeout);
			this.canvasDragDisabled = true;
			hotkeyTimeout = setTimeout(() => {
				this.canvasDragDisabled = false;
			}, 500);
			return false;
		}));

		this.hotkeysService.add(new Hotkey('escape', (event: KeyboardEvent): boolean => this.onEscapeKey(event)));

		this.hotkeysService.add(new Hotkey(['del', 'backspace'], (event: KeyboardEvent): boolean => {
			if (this.statsMode) {
				return true;
			}
			this.deleteItems(this.selection.items);
			return false;
		}));

		this.hotkeysService.add(new Hotkey('mod+z', (event: KeyboardEvent): boolean => {
			this.flowchartService.undo();
			return false;
		}));

		this.hotkeysService.add(new Hotkey('mod+shift+z', (event: KeyboardEvent): boolean => {
			this.flowchartService.redo();
			return false;
		}));
	}

	onEscapeKey(event: KeyboardEvent) {
		const ret = true;
		if (this.draggingNew || this.drawing) {
			this.drawing = false;
			this.draggingNew = false;
			this.updateAddButtonPosition(null);
			this.updateFlowchart(this.flowchart);
		} else if (this.selection.count) {
			this.flowchartService.clearSelection();
		}

		return ret;
	}

	ngOnDestroy() {
		if (this.projectWithStatsSubscription) {
			this.projectWithStatsSubscription.unsubscribe();
		}
		if (this.statsRefSub) {
			this.statsRefSub.unsubscribe();
		}
		if (this.stateSubscription) {
			this.stateSubscription.unsubscribe();
		}
		if (this.formChangeSubscription) {
			this.formChangeSubscription.unsubscribe();
		}
		if (this.selectionSubscription) {
			this.selectionSubscription.unsubscribe();
		}
		if (this.mainMediaClipSub) {
			this.mainMediaClipSub.unsubscribe();
		}
		if (this.flowchartZoomCanvas && this.flowchartZoomCanvas.canvasPositionObs) {
			this.flowchartZoomCanvas.canvasPositionObs.unsubscribe();
		}
	}

	stepDescriptionChanged(step: FlowchartStep) {
		if (step?.id && this.flowchart.steps[step.id]) {
			this.flowchart.steps[step.id].description = step.description;
		}
	}

	inspectorFormSubmit(ev: any) {
		ev.preventDefault();
		return false;
	}

	onSelectDragging(area: any) {
		if (!this.selectDragging && (area.width || area.height)) {
			this.selectDragging = true;
			// make tmp selection
			this.selection.items = { ...this.selection.items };
		}
		this.checkSelectCollision(this.flowchart.steps, area);
	}

	onSelectDragEnd() {
		if (this.selectDragging) {
			this.dragJustEnded = true;
			setTimeout(() => {
				this.dragJustEnded = false;
			}, 5);
			this.selectDragging = false;
			this.flowchartService.setSelection(this.selection.items);
		}
	}

	checkCollision(n: FlowchartStep, area: any) {
		return (
			n &&
			n.x + n.width > area.x &&
			n.x < area.x + area.width &&
			n.y + n.height > area.y &&
			n.y < area.y + area.height
		);
	}

	checkSelectCollision(steps: any, area: any) {
		Object.keys(steps).forEach((stepId: string) => {
			const n = steps[stepId];
			if (this.checkCollision(n, area)) {
				this.selection.items[stepId] = n;
			} else {
				delete this.selection.items[stepId];
			}
		});
	}

	stepEntityIdChanged(step: FlowchartStep) {
		if (step.isStart) {
			this.projectService.setMainMediaClipId(step.entityId);
		}
	}

	datesChanged(dateRange: DateRange) {
		this.projectService.getProjectStats(dateRange).subscribe();
	}

	statsUpdated() {
		if (!this.statsRef) {
			return;
		}

		this.maxViewCount = this.statsRef.maxViewCount;
		if (this.statsMode) {
			this.updateFlowchart(this.flowchart);
		}
	}

	setLineWidthToStep(step: FlowchartStep) {
		if (!this.statsMode) {
			return 1;
		} else {
			const ls = this.statsRef;
			setTimeout(() => {
				this.updateFlowchart(this.flowchart);
			}, 1);
			if (!step) {
				this.maxViewCount = Number(ls.maxViewCount);
				this.selectedStep = null;
				return;
			}

			if (step && step.entityId) {
				if (ls && ls.mediaclip && ls.mediaclip.views[step.entityId]) {
					this.maxViewCount = ls.mediaclip.views[step.entityId];
				}
			}
		}
	}

	selectItem(itemId: string | null) {
		const selectedItem = itemId ? { ...this.flowchart.steps[itemId] } : null;
		this.flowchartService.selectSingle(selectedItem);

		if (this.inspectorForm && this.inspectorForm.form && itemId) {
			this.inspectorForm.form.markAsPristine();
		}
	}

	createNew(generate?: boolean) {
		if (generate) {
			this.projectService.getOldProjectAndGenerate(this.gridRules).subscribe((generatedFlowchart: ProjectFlowchart) => {
				this.reduxService.dispatch({ history: 'reset', type: 'FROM_GENERATION', loadData: { data: generatedFlowchart } });
				this.positionCanvas();
			});
		} else {
			this.reduxService.dispatch({ history: 'reset', type: 'CREATE_NEW' });
		}
	}

	onZoomChange(zoom: number) {
		this.zoom = zoom;
	}

	updateFlowchart(flowchart: ProjectFlowchart) {
		this.highlightCols = [];
		this.ghostStep.x = 0; // hides the ghostStep
		this.toStep = null;
		this.fromStep = null;
		this.currentMouseOverStep = null;
		this.zoomOfsettedStepClone = null;

		this.noFlowchart = !flowchart || !flowchart.steps || Object.keys(flowchart.steps).length === 0;

		if (this.statsMode && this.noFlowchart && !this.generatedFlowchart) {
			return;
		}

		this.lines = {};
		if (this.noFlowchart) {
			return;
		}
		if (!this.flowchart.chapters) {
			this.flowchart.chapters = [];
		}
		flowchart.steps = { ...flowchart.steps };

		flowchart = this.projectService.positionBasedOnGrid(flowchart, this.gridRules);

		this.occupiedCellRef = {};
		Object.keys(flowchart.steps).forEach((stepId: string) => {
			const step = flowchart.steps[stepId];
			// set occupied cells
			if (step && typeof step.col !== 'undefined' && !this.occupiedCellRef[step.col]) {
				this.occupiedCellRef[step.col] = {};
			}
			if (step && typeof step.row !== 'undefined' && this.occupiedCellRef[step.col] && !this.occupiedCellRef[step.col][step.row]) {
				this.occupiedCellRef[step.col][step.row] = 1;
			}

			this.updateStepLinks(flowchart.steps[stepId], flowchart);
		});

		this.flowchart = flowchart;
		this.checkCanvasSize();

		if (this.selection && this.selection.single && this.selection.single.id) {
			const ns = flowchart.steps[this.selection.single.id];
			this.selectItem(ns ? ns.id : null);
		}
	}

	// For this step: check the inputs and outputs so we can update those lines
	updateStepLinks(step: FlowchartStep, flowchart: ProjectFlowchart) {
		const stepsToUpdate: {from: FlowchartStep; to: FlowchartStep}[] = [];
		if (step?.stepsOut) {
			step.stepsOut.forEach((toStepId: string) => {
				const toStep = flowchart.steps[toStepId];
				if (toStep) {
					stepsToUpdate.push({ from: step, to: toStep });
				}
			});
		}

		if (step?.stepsIn) {
			step.stepsIn.forEach((fromStepId: string) => {
				const fromStep = flowchart.steps[fromStepId];
				if (fromStep) {
					stepsToUpdate.push({ from: fromStep, to: step });
				}
			});
		}

		stepsToUpdate.forEach((stepToUpdate: any) => {
			this.createOrUpdateLink({
				fromStep: stepToUpdate.from,
				toStep: stepToUpdate.to
			});
		});
	}

	getEntityIdByStepId(stepId: string): string {
		return this.flowchart && this.flowchart.steps && this.flowchart.steps[stepId] && this.flowchart.steps[stepId].entityId;
	}

	createOrUpdateLink(props: any) {
		if (!props) {
			return;
		}

		if (!props.id) {
			props.id = props.fromStep.id + '_TO_' + props.toStep.id;
		}

		if (this.statsRef && !this.maxViewCount) {
			this.maxViewCount = Number(this.statsRef.maxViewCount);
		}

		// count
		if (!this.statsMode) {
			props.count = 0;
		} else {
			const count = this.statsRef?.link[props.fromStep?.entityId + '_TO_' + props.toStep?.entityId];
			props.count = count || 0;
		}

		// strokeColor
		if (props.toStep.x < props.fromStep.x) {
			props.strokeColor = '#E96E8E';
		} else if (!this.statsMode) {
			props.strokeColor = '#7AB1DF';
		} else {
			const linkId = props.fromStep?.entityId + '_TO_' + props.toStep?.entityId;
			if (!this.statsRef?.link[linkId]) {
				props.strokeColor = '#000000';
			} else {
				props.strokeColor = this.getColor(Number(this.statsRef.link[linkId]));
			}
		}

		// strokeWidth
		props.strokeWidth = this.getStrokeWidth(props);

		// statsMode
		if (this.statsMode && this.statsRef && this.statsRef.link) {
			props.offsetStart = 0;
			props.offsetEnd = this.maxStroke / 2;
			let currentLinkFound = false;
			let totalWidthOut = 0;
			const fromClipViews = props.fromStep?.entityId && this.statsRef?.mediaclip?.views[props.fromStep.entityId];

			// define width for each outStep line
			if (props.fromStep?.stepsOut?.length) {
				props.fromStep.width = this.projectService.getStepWidth();

				props.fromStep.stepsOut.forEach((toId: string, i: number) => {
					const linkOutWithStats: any = {};
					linkOutWithStats.linkId = props.fromStep.id + '_TO_' + toId;
					linkOutWithStats.toEntityId = this.getEntityIdByStepId(toId);
					linkOutWithStats.statsRefLinkId = props.fromStep.entityId + '_TO_' + linkOutWithStats.toEntityId;
					linkOutWithStats.statsCount = this.statsRef.link[linkOutWithStats.statsRefLinkId];

					const width = this.getStrokeWidthById(linkOutWithStats.statsRefLinkId);
					totalWidthOut += width;
					if (linkOutWithStats.linkId === props.id) {
						props.statsIndex = i;
						currentLinkFound = true;
					}

					if (!currentLinkFound && linkOutWithStats.statsCount && (!fromClipViews || fromClipViews <= this.maxViewCount)) {
						const ps = Math.round((Number(linkOutWithStats.statsCount) / this.maxViewCount) * this.maxStroke);
						props.offsetStart += ps;
					}
				});

				props.offsetStart += this.getStrokeWidth(props) / 2;
			}

			// create dropOff line (stopped at this video)
			const dropoffId = props.fromStep.id + '_TO_DROPOFF';
			const dropOffLine = { ...props };
			dropOffLine.toStep = {
				width: props.fromStep.width,
				height: props.fromStep.height,
				entityId: 'DROPOFF'
			};
			dropOffLine.isDropoff = true;
			dropOffLine.statsIndex = 500; // should be highest
			dropOffLine.id = dropoffId;

			const dropoffCount = this.statsRef.link[props.fromStep.entityId + '_TO_DROPOFF'];
			dropOffLine.count = dropoffCount || 0;
			const dropoffStrokeWidth = this.getStrokeWidth(dropOffLine);
			dropOffLine.strokeWidth = dropoffStrokeWidth;

			if (!fromClipViews || fromClipViews <= this.maxViewCount) {
				dropOffLine.offsetStart = totalWidthOut + (dropoffStrokeWidth / 2);
			}

			if (!this.lines[dropoffId]) {
				this.lines[dropoffId] = new FlowchartLink(dropOffLine);
			} else {
				this.lines[dropoffId].update(dropOffLine);
			}
		}

		if (this.lines[props.id]) {
			const id = props.id;
			delete props.id;
			this.lines[id].update(props);
		} else {
			const newLine = new FlowchartLink(props);
			this.lines[props.id] = newLine;
		}

		return this.lines[props.id];
	}

	tmpStepLink(fromStep: FlowchartStep, toStep: FlowchartStep) {
		if (!fromStep || !toStep) {
			return;
		}
		const lineId = fromStep.id + '_NEW';
		this.createOrUpdateLink({
			id: lineId,
			fromStep,
			toStep
		});

		return this.lines[lineId];
	}

	stepMouseOver(step: FlowchartStep) {
		this.mouseOverPlaceholder = false;

		if (this.mouseOutTimeout) {
			clearTimeout(this.mouseOutTimeout);
		}

		if (this.mouseOverPlaceholderTimeout) {
			clearTimeout(this.mouseOverPlaceholderTimeout);
		}

		if (this.drawing) {
			this.toStep = step;
			this.tmpStepLink(this.fromStep, this.toStep ? this.toStep : this.newStep);
		} else if (!this.dragging) {
			this.currentMouseOverStep = step;
			this.fromStep = step;
			this.updateAddButtonPosition(step);
		}
	}

	stepMouseOut(step: FlowchartStep) {
		this.toStep = null;
		this.currentMouseOverStep = null;
		if (!this.drawing && !this.dragging && !this.mouseOverPlaceholder) {
			this.mouseOutTimeout = setTimeout(() => {
				if (!this.drawing) {
					this.updateAddButtonPosition(null);
				}
			}, 300);
		}
	}

	updateAddButtonPosition(step: FlowchartStep, ev?: any) {
		if (!step) {
			this.newStep.x = -5000;
			this.newStep.y = -5000;
		} else {
			const n: any = {};
			n.x = ev ? ev.x : step.x;
			n.y = ev ? ev.y : step.y;
			if (ev && ev.stepOffsetY) { // is from currentMousePosition (dragging existing step)
				this.newStep.x = step.x + (step.width / 2) - 15;
				this.newStep.y = step.y - 12;
			} else {
				this.newStep.x = n.x + step.width - 15;
				this.newStep.y = n.y + step.height / 4 + 4;
			}
		}
	}

	setPlaceholderMouseOver(isMouseOver: boolean) {
		if (isMouseOver) {
			if (this.mouseOutTimeout) {
				clearTimeout(this.mouseOutTimeout);
			}
			if (this.mouseOverPlaceholderTimeout) {
				clearTimeout(this.mouseOverPlaceholderTimeout);
			}
			this.mouseOverPlaceholder = isMouseOver;
		} else {
			this.currentMouseOverStep = null;
			this.mouseOverPlaceholderTimeout = setTimeout(() => {
				this.mouseOverPlaceholder = isMouseOver;
				if (!this.currentMouseOverStep && !this.drawing) {
					this.updateAddButtonPosition(null);
				}
			}, 300);
		}
	}

	getColor(nr: number) {
		const range = [50, 190];
		const pct = nr / this.maxViewCount;
		const rgb = range[1] - Math.round(pct * (range[1] - range[0]));
		return `rgba(${rgb}, ${rgb}, ${rgb}, 0.7)`;
	}

	getStrokeWidth(line: any): number {
		if (!this.statsMode) {
			return 2;
		} else {
			let i = '';
			let fromId = '';
			if (line.fromStep?.entityId) {
				fromId = line.fromStep.entityId;
				i += line.fromStep.entityId;
			}
			i += '_TO_';
			if (line.toStep?.entityId) {
				i += line.toStep.entityId;
			}

			return this.getStrokeWidthById(i, fromId);
		}
	}

	getStrokeWidthById(linkId: string, fromId?: string): number { // format linkId: clipid_TO_clipid
		if (this.statsRef && !this.maxViewCount) {
			this.maxViewCount = Number(this.statsRef.maxViewCount);
		}
		if (!this.statsRef || !this.statsRef.link || !this.statsRef.link[linkId]) {
			return 0;
		} else {
			const ps = Math.round((Number(this.statsRef.link[linkId]) / this.maxViewCount) * this.maxStroke);
			const fromClipViews = fromId && this.statsRef && this.statsRef.mediaclip &&
				this.statsRef.mediaclip.views && this.statsRef.mediaclip.views[fromId];
			if (fromClipViews && fromClipViews > this.maxViewCount) {
				return 2.01;
			} else {
				return ps;
			}
		}
	}

	clickLine(line: FlowchartLink, ev: MouseEvent) {
		ev.stopPropagation();
		if (ev && ev.shiftKey) {
			this.flowchartService.toggleSelection(line);
		} else {
			this.flowchartService.selectSingle(line);
		}
	}

	canvasMouseOut(ev: MouseEvent) {
		this.canvasDragDisabled = true;
	}

	canvasMouseOver(ev: MouseEvent) {
		if (!this.dragging && !this.drawing) {
			this.canvasDragDisabled = false;
		}
	}

	canvasClick(ev: MouseEvent) {
		if (this.statsMode) {
			return false;
		}

		this.highlightStepText = false;
		this.checkLastChanges();
		if (!this.dragJustEnded) {
			this.flowchartService.clearSelection();
		}

		if (this.draggingNew) {
			this.onNewDragEnd(ev);
		}
	}

	canvasDblclick(ev: MouseEvent) {
		this.setLineWidthToStep(null);
	}

	setCurrentMousePosition(ev: MouseEvent, zoomFactor: number) {
		if (!this.canvasEl) {
			this.canvasEl = (ev.target as HTMLElement).closest('.flowchart-canvas') as HTMLElement;
		}

		const cbcr = this.canvasEl.getBoundingClientRect();
		this.currentMousePosition = {
			...this.currentMousePosition,
			...{
				x: (ev.clientX / this.zoomFactor) - (cbcr.left / this.zoomFactor),
				y: (ev.clientY / this.zoomFactor) - (cbcr.top / this.zoomFactor)
			}
		};
	}

	canvasMouseMove(ev: MouseEvent) {
		this.setCurrentMousePosition(ev, this.zoomFactor);
		if (this.draggingNew) {
			this.setNewStepPosition(ev.offsetX, ev.offsetY);
		}
	}

	checkLastChanges() {
		// handle previous selection
		const changedStep = { ...(this.selection && this.selection.single ? this.selection.single : this.selection.lastSelected) };
		if (this.inspectorForm && this.inspectorForm.dirty && changedStep) {
			this.updateStep(changedStep);
		}
	}

	stepMousedown(step: FlowchartStep, ev: MouseEvent) {
		const tb = ((ev.target as HTMLElement).closest('flowchart-step') as HTMLElement).getBoundingClientRect();

		this.currentMousePosition.stepOffsetX = ev.clientX - tb.left;
		this.currentMousePosition.stepOffsetY = ev.clientY - tb.top;

		if (ev.button === 0) { // left
			this.checkLastChanges();

			if (this.draggingNew) {
				this.onNewDragEnd(ev);
			} else {
				if (!this.dragJustEnded) {
					if (ev && ev.shiftKey) {
						this.flowchartService.toggleSelection(step);
					} else {
						if (this.selection.items[step.id]) {

						} else {
							this.flowchartService.selectSingle(step);
						}
					}
				}
			}
		}
	}

	stepMouseMove(step: FlowchartStep, ev?: MouseEvent) {
		if (!this.dragging && !this.drawing && !this.draggingNew) {
			this.currentMouseOverStep = step;
			this.fromStep = step;
			this.updateAddButtonPosition(step);
		}
	}

	stepClick(step: FlowchartStep, ev: MouseEvent) {
		this.highlightStepText = false;
		ev.stopPropagation();

		if (this.statsMode && step && step.entityId && !this.dragJustEnded) {
			this.setLineWidthToStep(step);
		}
	}

	showContextMenu(ev: MouseEvent) {
		ev.preventDefault();
		ev.stopPropagation();
		this.contextMenuOptions.push({
			label: 'UNDO',
			value: 'undo',
			icon: 'undo'
		});
		this.contextMenuOptions.push({
			label: 'REDO',
			value: 'redo',
			icon: 'repeat'
		});
		this.contextMenuStyle.display = 'block';
		this.contextMenuStyle.left = this.currentMousePosition.x + 'px';
		this.contextMenuStyle.top = this.currentMousePosition.y + 'px';
	}

	canvasContextMenu(ev: MouseEvent) {
		if (this.statsMode) {
			return;
		}

		if (this.selection.count) {
			this.contextMenuOptions = [{
				label: 'DELETE_SELECTED_ITEMS',
				value: 'delete_selection',
				icon: 'trash'
			}];
		} else {
			this.contextMenuOptions = [];
		}
		this.showContextMenu(ev);
		return false;
	}

	lineContextMenu(line: FlowchartLink, ev: MouseEvent) {
		this.contextMenuOptions = [{
			item: line,
			label: 'DELETE_LINK',
			value: 'delete',
			icon: 'trash'
		}];
		this.showContextMenu(ev);
		return false;
	}

	stepContextMenu(step: FlowchartStep, ev: MouseEvent) {
		if (step.isStart) {
			ev.preventDefault();
			ev.stopPropagation();
			return false;
		}
		this.contextMenuOptions = [{
			item: step,
			label: 'DELETE_STEP',
			value: 'delete',
			icon: 'trash'
		}];
		this.showContextMenu(ev);
		return false;
	}

	contextMenuHide() {
		this.contextMenuStyle.display = 'none';
		this.contextMenuOptions = [];
	}

	contextMenuOptionSelected(menuItem: ContextMenuOption) {
		switch (menuItem.value) {
			case 'delete':
				if (menuItem.item && menuItem.item.type === 'FlowchartStep') {
					this.deleteStep(menuItem.item);
				}
				if (menuItem.item && menuItem.item.type === 'FlowchartLink') {
					const items = {};
					items[menuItem.item.id] = menuItem.item;
					this.deleteItems(items);
				}
				break;

			case 'delete_selection':
				this.deleteItems(this.selection.items);
				break;

			case 'add_step':
				this.onNewDragStart();
				this.draggingNew = true;
				this.newStep.width = 140;
				this.newStep.height = 60;
				this.newStep.x = 40;
				this.newStep.y = 40;
				break;

			case 'undo':
				this.flowchartService.undo();
				break;

			case 'redo':
				this.flowchartService.redo();
				break;
		}
		this.contextMenuStyle.display = 'none';
	}

	stepDblclick(step: FlowchartStep, ev: MouseEvent) {
		this.highlightStepText = false;
		ev.stopPropagation();

		this.setLineWidthToStep(step);
		if (this.selection.count !== 1) {
			this.flowchartService.selectSingle(step);
		}
		this.setFocus(true);

		setTimeout(() => {
			this.updateFlowchart(this.flowchart);
		}, 350);
	}

	onNewClick(ev: any) {
		this.highlightStepText = false;
		this.onNewDragStart();
		this.draggingNew = true;
		this.newStep.width = 140;
		this.newStep.height = 60;
		const gp = this.getClosestGridPoint(
			this.fromStep.x + this.gridRules.x.gutter + this.gridRules.x.track,
			this.fromStep.y
		);
		this.newStep.x = gp.x;
		this.newStep.y = gp.y;
	}

	onNewDragStart(ev?: any) {
		delete this.ghostStep.fromId;
		if (this.mouseOutTimeout) {
			clearTimeout(this.mouseOutTimeout);
		}
		if (this.mouseOverPlaceholderTimeout) {
			clearTimeout(this.mouseOverPlaceholderTimeout);
		}
		this.drawing = true;
	}

	// Set this.newStep position based on grid lines
	setNewStepPosition(offsetX: number, offsetY: number) {
		this.newStep.width = 140;
		this.newStep.height = 60;
		this.newStep = { ...this.newStep, ...this.getClosestGridPoint(offsetX, offsetY) };

		this.tmpStepLink(this.fromStep, this.toStep ? this.toStep : this.newStep);
	}

	// Adding new or connecting two steps
	onNewDragEnd(ev?: any) {
		if (this.toStep) {
			if (this.toStep !== this.fromStep) {
				if (!this.fromStep || !this.toStep) {
					return;
				}
				const stepLink = this.tmpStepLink(this.fromStep, this.toStep);
				this.reduxService.dispatch({
					type: 'ADD_STEP_LINK',
					fromStep: this.fromStep, toStep: this.toStep, stepLink
				});
			}
		} else {
			const gp = this.getClosestGridPoint(this.newStep.x, this.newStep.y);
			this.newStep = { ...this.newStep, ...gp };
			this.createNewStep(this.newStep);
		}

		this.drawing = false;
		this.draggingNew = false;
	}

	getTrackPoint(coord: number, rules: any) {
		const trackAndGutter = rules.track + rules.gutter;
		const rest = (coord - rules.start) % trackAndGutter;
		const snapDiff = rest > (trackAndGutter / 2) ? trackAndGutter - rest : rest * -1;
		const ret = coord + snapDiff; // snap to grid
		return ret;
	}

	getClosestGridPoint(x: number, y: number, step?: FlowchartStep, movingToClosest?: boolean, allowSamePosition?: boolean): any {
		const ret: any = {};
		const colWidth = (this.gridRules.x.track + this.gridRules.x.gutter);
		const rowHeight = (this.gridRules.y.track + this.gridRules.y.gutter);
		x = x < this.gridRules.x.start ? this.gridRules.x.start : x;
		y = y < this.gridRules.y.start ? this.gridRules.y.start : y;

		if (step) {

			if (!movingToClosest && step.width) {
				x -= step.width / 2;
			}

			if (!movingToClosest && step.height) {
				y -= step.height / 2;
			}

			if (step.isStart) {
				// startStep should stay in first column
				x = 1;
			} else if (x < colWidth) {
				// no other steps allowed in start column
				x = colWidth;
			}

			if (this.statsMode) {
				// keep step in its column in statsMode
				x = step.col * colWidth;
			}
		}

		ret.x = this.getTrackPoint(x, this.gridRules.x);
		ret.col = (ret.x - this.gridRules.x.start) / colWidth;
		if (!this.ghostStep.fromId || (step && step.id === this.ghostStep.fromId)) {
			this.ghostStep.x = ret.x;
		}

		if (this.statsMode) {
			ret.y = y;
			ret.row = (ret.y) / rowHeight;
			if (!this.ghostStep.fromId || (step && step.id === this.ghostStep.fromId)) {
				this.ghostStep.y = ret.y;
			}
			return ret;
		}
		let checkY = this.getTrackPoint(y, this.gridRules.y);
		let checkRow = (checkY) / rowHeight;
		let retryCount = 8;

		if (!allowSamePosition) {
			// move the step down if it lands on another step
			while (this.occupiedCellRef[ret.col] && this.occupiedCellRef[ret.col][checkRow] && retryCount > 0) {
				checkY = this.getTrackPoint(checkY + rowHeight, this.gridRules.y);
				checkRow = checkY / rowHeight;
				retryCount--;
			}
		}

		ret.y = this.getTrackPoint(checkY, this.gridRules.y);
		ret.row = (ret.y) / rowHeight;
		if (ret.col >= this.flowchart.chapters.length) {
			this.highlightCols.length = ret.col - this.flowchart.chapters.length + 1;
		}
		if (!this.ghostStep.fromId || (step && step.id === this.ghostStep.fromId)) {
			this.ghostStep.y = ret.y;
		}
		return ret;
	}

	createNewStep(props: FlowchartStep) {
		const newStep = new FlowchartStep(props);
		newStep.description = 'New ' + (Object.keys(this.flowchart.steps).length + 1);

		if (!this.fromStep) {
			return;
		}
		this.reduxService.dispatch({ type: 'ADD_STEP', step: newStep, fromStep: this.fromStep, chapters: this.flowchart.chapters });
		setTimeout(() => {
			// setTimeout so the canvas click deselect doesn't directly undo the select
			this.selectItem(newStep.id);
			this.setFocus(true);

			if (this.checkCollision(this.selection.single, { x: this.currentMousePosition.x, y: this.currentMousePosition.y, width: 1, height: 1 })) {
				this.updateAddButtonPosition(this.selection.single);
			} else {
				this.updateAddButtonPosition(null);
			}
		}, 50);
		this.fromStep = null;

	}

	onStepDragStart(step: FlowchartStep, ev: any) {
		this.ghostStep.fromId = step.id;
		Object.keys(this.selection.items).forEach((itemId) => {
			const selectedStep = this.flowchart.steps[itemId];
			if (selectedStep && selectedStep.col > -1 && selectedStep.row > -1) {
				// remove selected step from occupied
				this.occupiedCellRef[selectedStep.col][selectedStep.row] = 0;
			}
		});
		if (step && step.col > -1 && step.row > -1) {
			// remove current step from occupied
			this.occupiedCellRef[step.col][step.row] = 0;
		}
		this.zoomOfsettedStepClone = { ...step };
		this.dragPrevPos.x = step.x;
		this.dragPrevPos.y = step.y;
		this.dragging = true;
	}

	setClonePosition() {
		if (this.zoomOfsettedStepClone && this.currentMousePosition) {
			this.zoomOfsettedStepClone.x = this.currentMousePosition.x;
			this.zoomOfsettedStepClone.y = this.currentMousePosition.y;
			if (this.currentMousePosition.stepOffsetX) {
				this.zoomOfsettedStepClone.x = this.currentMousePosition.x - this.currentMousePosition.stepOffsetX +
					(this.zoomOfsettedStepClone.width / 2);
				this.zoomOfsettedStepClone.y = this.currentMousePosition.y - this.currentMousePosition.stepOffsetY +
					(this.zoomOfsettedStepClone.height / 2);
			}
		}
	}

	onStepDragging(step: FlowchartStep, ev?: any) {
		const c = { ...step };
		this.setClonePosition();

		// hide add button when dragging existing block
		this.newStep.x = -5000;
		this.newStep.y = -5000;

		if (this.zoomOfsettedStepClone) {
			const gp = this.getClosestGridPoint(this.zoomOfsettedStepClone.x, this.zoomOfsettedStepClone.y, step);
			c.x = gp.x;
			c.y = gp.y;

			if (this.toStep) {
				c.stepsOut = [...c.stepsOut];
				c.stepsOut.push(this.toStep.id);
			}
			const n = new FlowchartStep(c);
			if (this.selection.count > 1) {
				this.updateGroupDragPositions(n);
			} else {
				this.updateStepLinks(n, this.flowchart);
			}
		}
	}

	updateGroupDragPositions(dragStep: FlowchartStep) {
		this.groupDragDelta = {
			x: dragStep.x - this.dragPrevPos.x,
			y: dragStep.y - this.dragPrevPos.y
		};
		Object.keys(this.selection.items).forEach((itemId) => {
			const item = { ...this.flowchart.steps[itemId] };
			if (item && item.type === 'FlowchartStep') {
				item.x += this.groupDragDelta.x;
				item.y += this.groupDragDelta.y;
				this.flowchart.steps[itemId] = this.moveToClosestGridPoint(item);
				this.updateStepLinks(item, this.flowchart);
			}
		});
		this.dragPrevPos.x = dragStep.x;
		this.dragPrevPos.y = dragStep.y;
	}

	onStepDragEnd(step: FlowchartStep, ev: any) {
		this.dragJustEnded = true;
		this.highlightCols = [];

		const gp = this.getClosestGridPoint(this.zoomOfsettedStepClone && this.zoomOfsettedStepClone.x,
			this.zoomOfsettedStepClone && this.zoomOfsettedStepClone.y, step);
		setTimeout(() => {
			// angular doesn't update the DOM correctly so this hack is needed unfortunately to trigger another update.
			// This moves the step to the correct position (is the same in redux)
			const s = this.flowchart.steps[step.id];
			if (gp.y) {
				s.y = gp.y - 0.1;
			}
			if (gp.x) {
				s.x = gp.x - 0.1;
			}
			setTimeout(() => {
				if (gp.y) {
					s.y = gp.y;
				}
				if (gp.x) {
					s.x = gp.x;
				}
			}, 1);
			this.dragJustEnded = false;
		}, 5);
		this.dragging = false;

		const changedStep = { ...step };
		if (ev.top) {
			changedStep.y = gp.y;
		}
		if (ev.left) {
			changedStep.x = gp.x;
		}
		if (this.selection.count > 1) {
			const itemsToUpdate = {};
			Object.keys(this.selection.items).forEach((itemId) => {
				itemsToUpdate[itemId] = { ...this.flowchart.steps[itemId] };
				itemsToUpdate[itemId] = this.moveToClosestGridPoint(itemsToUpdate[itemId]);
			});

			this.updateMultipleSteps(itemsToUpdate);
		} else {
			this.updateStep(changedStep);
		}
	}

	moveToClosestGridPoint(step: FlowchartStep, allowSamePosition?: boolean) {
		const gp = this.getClosestGridPoint(step.x, step.y, step, true, allowSamePosition);
		return { ...step, ...gp };
	}

	inspectorFormChanged(form: NgForm, changes: any) {
		const changedStep = { ...(this.selection && this.selection.single ? this.selection.single : this.selection.lastSelected) };

		if (form.dirty && changedStep) {
			this.updateStep(changedStep);
		}
	}

	inspectorFormKeyDown(ev: KeyboardEvent) {
		this.highlightStepText = false;
		if (ev.code === 'Enter' || ev.code === 'NumpadEnter') {
			ev.preventDefault();
			const changedStep = { ...(this.selection && this.selection.single ? this.selection.single : this.selection.lastSelected) };
			if (this.inspectorForm.dirty && changedStep) {
				this.updateStep(changedStep);
			}
			return false;
		}
	}

	onStepClipSelected(selectedOption: any) {
		this.useClipTitleAsStepTitle(this.selection.single, selectedOption);
	}

	useClipTitleAsStepTitle(step: FlowchartStep, mediaclip: Mediaclip, force: boolean = false) {
		if (mediaclip && mediaclip.title && step && (this.stepTitleIsNew(step.description) || force)) {
			step.description = mediaclip.title;
			this.stepDescriptionChanged(step);
			this.updateStep(step);
		}
	}

	updateStep(step: any) {
		const gpStep = this.moveToClosestGridPoint(step, true);
		this.ghostStep.x = -1000; // hides the ghostStep
		const changedStep = { ...this.flowchart.steps[step.id], ...gpStep }; // closest gridpoint and row and col
		this.reduxService.dispatch({ type: 'UPDATE_STEP', step: changedStep, chapters: [...this.flowchart.chapters] });
	}

	updateMultipleSteps(stepsRef: any) {
		this.reduxService.dispatch({ type: 'UPDATE_MULTIPLE_STEPS', steps: stepsRef });
	}

	deleteStep(step: FlowchartStep) {
		if (step.isStart) {
			this.projectService.setMainMediaClipId(null);
		}
		this.reduxService.dispatch({ type: 'DELETE_STEP', step });
	}

	deleteItems(itemsRef: any) {
		if (itemsRef && Object.keys(itemsRef).length) {
			if (Object.keys(itemsRef).length === 1 && itemsRef[Object.keys(itemsRef)[0]] && itemsRef[Object.keys(itemsRef)[0]].isStart) {

			} else {
				this.flowchartService.clearSelection();
				this.reduxService.dispatch({ type: 'DELETE_ITEMS', items: itemsRef });
			}
		}
	}

	stopPropagation(ev: Event) {
		ev.stopPropagation();
	}

	trackBy(index: number, item: any) {
		return item.id || index;
	}
}
